/**
 * Hyreskontrakt för studentbostad (Formulär R-14).
 *
 * The bostadshyra structure adapted for student housing: use locked to
 * studentbostad, corridor rooms with shared facilities, corridor cleaning,
 * part-year rent and the study-time rule. Slot keys and option values/codes
 * are the legacy template's. Clause text verbatim from
 * `Hyreskontrakt för studentbostad` (Haymanot-approved).
 */
import { list, nodes, p, t, table } from '@hyranai/regta-document-ir';
import { customSectionsBlockDefinition } from '../common/custom-sections.js';
import { partyDetailsBlock, referenceLine } from '../common/fields.js';
import { signingBlockDefinition } from '../common/signing.js';
import { dateVal, numVal, optionChosen, optionPrinted, optionVal, propertySlot, shown, textVal, toSlotOptions, whenIncludes, whenOption, } from '../common/slots.js';
import { box, optionOf, rows, sectionBlock, unnumberedClause } from '../bostadshyra/kit.js';
import { APARTMENT_TYPE_OPTIONS, COLD_WATER_OPTIONS, CORRIDOR_BATH_OPTIONS, CORRIDOR_CLEANING_OPTIONS, CORRIDOR_KITCHEN_OPTIONS, CORRIDOR_WC_OPTIONS, DEPOSIT_OPTIONS, ELECTRICITY_OPTIONS, HEATING_OPTIONS, HOT_WATER_OPTIONS, LEASE_TYPE_OPTIONS, MEMBER_FEE_OPTIONS, MEMBERSHIP_OPTIONS, NOTICE_TYPE_OPTIONS, PARKING_OPTIONS, PAYMENT_TYPE_OPTIONS, RENT_MODEL_OPTIONS, RENT_TYPE_OPTIONS, SPARE_KEY_OPTIONS, TENANT_ORG_OPTIONS, } from './options.js';
const REVIEW = { reviewedBy: 'Haymanot', source: 'base-templates/Hyreskontrakt för studentbostad – kopiera.docx (Haymanot-approved)' };
const K = 'studentbostad';
export const avtalsuppgifter = {
    blockKey: `${K}.avtalsuppgifter`,
    version: 'v1',
    plane: 'shared',
    status: 'published',
    language: 'sv-SE',
    title: 'Avtalsuppgifter',
    summary: 'The contract reference number printed above the preamble.',
    slots: [{ key: 'contract_number', label: 'Avtalsnummer', type: 'text', required: true, help: 'Egen referens på avtalet, t.ex. S-2042.' }],
    compile: (input) => nodes(referenceLine([['Avtalsnummer', textVal(input, 'contract_number')]])),
};
export const hyresvard = partyDetailsBlock({
    blockKey: `${K}.hyresvard`,
    variant: 'inline',
    ref: 'hyresvard',
    title: 'Hyresvärd',
    summary: 'The landlord: name, organisation number, address and phone.',
    legalReview: REVIEW,
    groups: [
        {
            role: 'lessor',
            ordinal: 0,
            fields: [
                { key: 'landlord_name', label: 'Namn', field: 'name', required: true },
                { key: 'landlord_org_number', label: 'Organisationsnummer', field: 'legalId', required: true },
                { key: 'landlord_address', label: 'Adress', field: 'address' },
                { key: 'landlord_phone', label: 'Telefon', field: 'phone' },
            ],
        },
    ],
});
export const hyresgast = partyDetailsBlock({
    blockKey: `${K}.hyresgast`,
    variant: 'inline',
    ref: 'hyresgast',
    title: 'Hyresgäst',
    summary: 'The student tenant: name, personnummer, address and phone.',
    legalReview: REVIEW,
    groups: [
        {
            role: 'lessee',
            ordinal: 0,
            fields: [
                { key: 'tenant_name', label: 'Namn', field: 'name', required: true },
                { key: 'tenant_personnummer', label: 'Personnummer', field: 'legalId', required: true, help: 'PII — ej sökbart' },
                { key: 'tenant_address', label: 'Adress', field: 'address' },
                { key: 'tenant_phone', label: 'Telefon', field: 'phone' },
            ],
        },
    ],
});
export const lagenhetensAdress = sectionBlock({
    blockKey: `${K}.lagenhets-adress`,
    ref: 'lagenhets-adress',
    title: 'Lägenhetens adress',
    summary: 'Where the apartment is: municipality, property designation, floor, apartment numbers and postal address.',
    legalReview: REVIEW,
    slots: [
        propertySlot('property_municipality', 'Kommun', 'municipality', { required: true }),
        propertySlot('property_designation', 'Fastighetsbeteckning', 'designation', { required: true }),
        { key: 'property_staircase', label: 'Trappor', type: 'text' },
        { key: 'property_landlord_apt_no', label: 'Hyresvärdens lägenhetsnummer', type: 'text' },
        propertySlot('property_address', 'Postadress', 'address', { type: 'textarea', required: true }),
        { key: 'property_official_apt_no', label: 'Officiellt lägenhetsnummer', type: 'text' },
    ],
    body: (input) => rows([
        ['Kommun', [textVal(input, 'property_municipality')]],
        ['Fastighetsbeteckning', [textVal(input, 'property_designation')]],
        ['Trappor', [textVal(input, 'property_staircase')]],
        ['Hyresvärdens lägenhetsnummer', [textVal(input, 'property_landlord_apt_no')]],
        ['Postadress', [textVal(input, 'property_address')]],
        ['Officiellt lägenhetsnummer', [textVal(input, 'property_official_apt_no')]],
    ]),
});
export const anvandning = sectionBlock({
    blockKey: `${K}.anvandning`,
    ref: 'anvandning',
    title: 'Lägenhetens användning',
    summary: 'The apartment is let for use as student housing (fixed text).',
    legalReview: REVIEW,
    slots: [],
    body: () => [p('Lägenheten hyrs ut för att användas till studentbostad.')],
});
const isCorridor = (input) => input.is_corridor_room === true;
const BATH_PAIR = [optionOf(CORRIDOR_BATH_OPTIONS, 'privat'), optionOf(CORRIDOR_BATH_OPTIONS, 'gemensamt')];
const WC_PAIR = [optionOf(CORRIDOR_WC_OPTIONS, 'privat'), optionOf(CORRIDOR_WC_OPTIONS, 'gemensam')];
/** "☑ Privat badrum ☐ Gemensamt badrum (delas med: N rum)". */
function facilityLine(ctx, input, key, options, countKey) {
    const [privat, shared] = options;
    const sharedOn = optionChosen(options, input[key], shared);
    return [
        box(ctx, optionChosen(options, input[key], privat), key),
        t(` ${optionPrinted(privat)}   `),
        box(ctx, sharedOn, key),
        t(` ${optionPrinted(shared)}`),
        ...(shown(ctx, sharedOn) ? [t(' (delas med: '), numVal(ctx, input, countKey), t(' rum)')] : []),
    ];
}
export const storlek = sectionBlock({
    blockKey: `${K}.storlek`,
    ref: 'storlek',
    title: 'Lägenhetstyp och storlek',
    summary: 'A corridor room (bath, wc, kitchen, room count, shared facilities) or a whole apartment (rooms and kitchen), and the area.',
    legalReview: REVIEW,
    slots: [
        { key: 'is_corridor_room', label: 'Rum i korridor', default: true, type: 'boolean', help: 'avser ett rum i korridor' },
        {
            key: 'corridor_bath',
            label: 'Badrum',
            type: 'multi_select',
            options: toSlotOptions(CORRIDOR_BATH_OPTIONS),
            help: 'Kan välja både privat och gemensamt.',
            visibleWhen: { key: 'is_corridor_room', equals: true },
        },
        {
            key: 'corridor_bath_shared_count',
            label: 'Gemensamt badrum delas med (antal rum)',
            type: 'number',
            visibleWhen: { all: [{ key: 'is_corridor_room', equals: true }, whenIncludes('corridor_bath', optionOf(CORRIDOR_BATH_OPTIONS, 'gemensamt'))] },
        },
        {
            key: 'corridor_wc',
            label: 'WC',
            type: 'multi_select',
            options: toSlotOptions(CORRIDOR_WC_OPTIONS),
            help: 'Kan välja både privat och gemensam.',
            visibleWhen: { key: 'is_corridor_room', equals: true },
        },
        {
            key: 'corridor_wc_shared_count',
            label: 'Gemensam wc delas med (antal rum)',
            type: 'number',
            visibleWhen: { all: [{ key: 'is_corridor_room', equals: true }, whenIncludes('corridor_wc', optionOf(CORRIDOR_WC_OPTIONS, 'gemensam'))] },
        },
        {
            key: 'corridor_kitchen',
            default: 'endast_gemensamt',
            label: 'Kök',
            type: 'select',
            required: true,
            options: toSlotOptions(CORRIDOR_KITCHEN_OPTIONS),
            visibleWhen: { key: 'is_corridor_room', equals: true },
        },
        { key: 'corridor_room_count', label: 'Antal rum i korridor', type: 'number', required: true, visibleWhen: { key: 'is_corridor_room', equals: true } },
        {
            key: 'apartment_type',
            label: 'Antal rum och kök',
            type: 'select',
            required: true,
            options: toSlotOptions(APARTMENT_TYPE_OPTIONS),
            visibleWhen: { key: 'is_corridor_room', equals: false },
        },
        { key: 'apartment_area', label: 'Area (m²)', type: 'number', required: true },
        {
            key: 'shared_facilities',
            label: 'Gemensamma utrymmen i korridoren',
            type: 'textarea',
            help: 'Beskriv vilka utrymmen hyresgästen tillsammans med övriga studenter har gemensam tillgång till (t.ex. kök, badrum, gemensamhetsutrymme).',
            visibleWhen: { key: 'is_corridor_room', equals: true },
        },
    ],
    body: (input, ctx) => {
        const corridor = shown(ctx, isCorridor(input));
        return [
            ...rows([
                ['Rum i korridor', [box(ctx, isCorridor(input), 'is_corridor_room')]],
                corridor && ['Antal rum i korridor', [numVal(ctx, input, 'corridor_room_count')]],
                corridor && [
                    'Badrum',
                    isCorridor(input)
                        ? facilityLine(ctx, input, 'corridor_bath', BATH_PAIR, 'corridor_bath_shared_count')
                        : [],
                ],
                corridor && [
                    'WC',
                    isCorridor(input)
                        ? facilityLine(ctx, input, 'corridor_wc', WC_PAIR, 'corridor_wc_shared_count')
                        : [],
                ],
                corridor && ['Kök', [optionVal(CORRIDOR_KITCHEN_OPTIONS, input, 'corridor_kitchen')]],
                shown(ctx, input.is_corridor_room === false) && ['Antal rum och kök', [optionVal(APARTMENT_TYPE_OPTIONS, input, 'apartment_type')]],
                ['Area (m²)', [numVal(ctx, input, 'apartment_area')]],
            ]),
            ...nodes(corridor &&
                p([
                    t('Utöver ovan angivet rum i korridor m.m. har hyresgästen tillsammans med övriga studenter i korridoren gemensam tillgång till '),
                    textVal(input, 'shared_facilities', 'long'),
                    t('.'),
                ])),
        ];
    },
});
export const tillbehor = sectionBlock({
    blockKey: `${K}.tillbehor`,
    ref: 'tillbehor',
    title: 'Till lägenheten hör',
    summary: 'Whether an attic, basement or other storage space comes with the apartment, and their numbers.',
    legalReview: REVIEW,
    slots: [
        { key: 'has_attic', label: 'Vindsutrymme ingår', type: 'boolean' },
        { key: 'attic_number', label: 'Vindsutrymme nr', type: 'text', visibleWhen: { key: 'has_attic', equals: true } },
        { key: 'has_basement', label: 'Källarutrymme ingår', type: 'boolean' },
        { key: 'basement_number', label: 'Källarutrymme nr', type: 'text', visibleWhen: { key: 'has_basement', equals: true } },
        { key: 'has_other_storage', label: 'Annat utrymme ingår', type: 'boolean' },
        { key: 'other_storage_number', label: 'Annat utrymme nr', type: 'text', visibleWhen: { key: 'has_other_storage', equals: true } },
    ],
    body: (input, ctx) => {
        const cell = (flag, label, numberKey) => rows([
            [label, [box(ctx, input[flag] === true, flag)]],
            shown(ctx, input[flag] === true) && [`${label} nr`, [textVal(input, numberKey)]],
        ]);
        return [
            table([
                { key: 'attic', width: 33 },
                { key: 'basement', width: 33 },
                { key: 'other', width: 34 },
            ], [
                [
                    cell('has_attic', 'Vindsutrymme', 'attic_number'),
                    cell('has_basement', 'Källarutrymme', 'basement_number'),
                    cell('has_other_storage', 'Annat utrymme', 'other_storage_number'),
                ],
            ], { variant: 'plain' }),
        ];
    },
});
const isFixedTerm = (input) => optionChosen(LEASE_TYPE_OPTIONS, input.lease_type, 'bestamd_tid');
export const hyrestid = sectionBlock({
    blockKey: `${K}.hyrestid`,
    ref: 'hyrestid',
    title: 'Hyrestid',
    summary: 'Open-ended or fixed-term lease, with start and (for a fixed term) end date.',
    legalReview: REVIEW,
    slots: [
        { key: 'lease_type', label: 'Hyresform', default: 'bestamd_tid', type: 'select', required: true, options: toSlotOptions(LEASE_TYPE_OPTIONS) },
        { key: 'lease_start_date', label: 'Startdatum', type: 'date', required: true },
        { key: 'lease_end_date', label: 'Slutdatum', type: 'date', visibleWhen: whenOption('lease_type', optionOf(LEASE_TYPE_OPTIONS, 'bestamd_tid')) },
    ],
    body: (input, ctx) => rows([
        ['Hyresform', [optionVal(LEASE_TYPE_OPTIONS, input, 'lease_type')]],
        ['Startdatum', [dateVal(ctx, input, 'lease_start_date')]],
        shown(ctx, isFixedTerm(input)) && ['Slutdatum', [dateVal(ctx, input, 'lease_end_date')]],
    ]),
});
export const uppsagning = sectionBlock({
    blockKey: `${K}.uppsagning`,
    ref: 'uppsagning',
    title: 'Uppsägning',
    summary: 'Notice period and, for a fixed term, the automatic extension period with its notice rule.',
    legalReview: REVIEW,
    slots: [
        { key: 'notice_type', label: 'Uppsägningstyp', default: 'tre_manader', type: 'select', required: true, options: toSlotOptions(NOTICE_TYPE_OPTIONS) },
        { key: 'notice_months', label: 'Antal månader', type: 'number', visibleWhen: whenOption('notice_type', optionOf(NOTICE_TYPE_OPTIONS, 'annat_antal')) },
        {
            key: 'extension_months',
            default: 3,
            label: 'Förlängning (månader)',
            type: 'number',
            required: true,
            help: 'Hur lång period kontraktet förlängs med i taget om uppsägning inte sker.',
            visibleWhen: whenOption('lease_type', optionOf(LEASE_TYPE_OPTIONS, 'bestamd_tid')),
        },
    ],
    body: (input, ctx) => {
        const fixed = shown(ctx, isFixedTerm(input));
        return [
            ...rows([
                ['Uppsägningstyp', [optionVal(NOTICE_TYPE_OPTIONS, input, 'notice_type')]],
                shown(ctx, optionChosen(NOTICE_TYPE_OPTIONS, input.notice_type, 'annat_antal')) && ['Antal månader', [numVal(ctx, input, 'notice_months')]],
                fixed && ['Förlängning (månader)', [numVal(ctx, input, 'extension_months')]],
            ]),
            ...nodes(fixed &&
                p([
                    t('Uppsägning ska ske skriftligen tidigast till månadsskifte som inträffar närmast efter tre månader från uppsägningen. I annat fall är kontraktet förlängt med '),
                    numVal(ctx, input, 'extension_months'),
                    t(' månad(er) för varje gång.'),
                ])),
        ];
    },
});
const isKollektiv = (input) => optionChosen(RENT_MODEL_OPTIONS, input.rent_model, 'kollektiva');
export const hyressattningsmodell = sectionBlock({
    blockKey: `${K}.hyressattningsmodell`,
    ref: 'hyressattningsmodell',
    title: 'Hyressättningsmodell',
    summary: 'How the rent is set: collective negotiation, a time-limited rent-setting agreement (referenced), or direct negotiation.',
    legalReview: REVIEW,
    slots: [
        { key: 'rent_model', label: 'Hyressättningsmodell', default: 'kollektiva', type: 'select', required: true, options: toSlotOptions(RENT_MODEL_OPTIONS) },
        {
            key: 'rent_model_annex',
            label: 'Bilaga (hyressättningsavtal)',
            type: 'text',
            help: 'Hänvisning till bifogat hyressättningsavtal.',
            visibleWhen: whenOption('rent_model', optionOf(RENT_MODEL_OPTIONS, 'tidsbestamt_avtal')),
        },
    ],
    body: (input, ctx) => rows([
        ['Modell', [optionVal(RENT_MODEL_OPTIONS, input, 'rent_model')]],
        shown(ctx, optionChosen(RENT_MODEL_OPTIONS, input.rent_model, 'tidsbestamt_avtal')) && [
            'Bilaga (hyressättningsavtal)',
            [textVal(input, 'rent_model_annex')],
        ],
    ]),
});
export const forhandlingsklausul = sectionBlock({
    blockKey: `${K}.forhandlingsklausul`,
    ref: 'forhandlingsklausul',
    title: 'Förhandlingsklausul',
    summary: 'The negotiation clause and tenant organisation. Only present under collective negotiation.',
    legalReview: REVIEW,
    slots: [
        {
            key: 'negotiation_clause',
            default: true,
            label: 'Förhandlingsklausul gäller',
            type: 'boolean',
            help: 'Endast aktuellt vid kollektiva hyresförhandlingar.',
            visibleWhen: whenOption('rent_model', optionOf(RENT_MODEL_OPTIONS, 'kollektiva')),
        },
        {
            key: 'tenant_org',
            label: 'Hyresgästorganisation',
            type: 'select_with_freetext',
            options: toSlotOptions(TENANT_ORG_OPTIONS),
            visibleWhen: whenOption('rent_model', optionOf(RENT_MODEL_OPTIONS, 'kollektiva')),
        },
    ],
    body: (input, ctx) => shown(ctx, isKollektiv(input))
        ? [
            ...rows([
                ['Förhandlingsklausul gäller', [box(ctx, input.negotiation_clause === true, 'negotiation_clause')]],
                ['Hyresgästorganisation', [optionVal(TENANT_ORG_OPTIONS, input, 'tenant_org')]],
            ]),
            p('Förhandlingsklausul gäller för hyresavtalet, vilket innebär att hyran för lägenheten förhandlas fram med den hyresgästorganisation som är part i den för huset gällande förhandlingsordningen.'),
        ]
        : null,
});
export const hyra = sectionBlock({
    blockKey: `${K}.hyra`,
    ref: 'hyra',
    title: 'Hyra',
    summary: 'Monthly rent, optional part-year rent (rent-free months), rent type, and tenant-organisation fee/membership.',
    legalReview: REVIEW,
    slots: [
        { key: 'rent_amount', label: 'Hyra (kr/mån)', type: 'currency', required: true },
        { key: 'rent_partial_year', label: 'Hyra utgår endast under del av året', default: false, type: 'boolean', help: 'Kryssa i om hyra endast utgår vissa månader per år.' },
        { key: 'rent_months_per_year', label: 'Antal månader per år hyra utgår', type: 'number', visibleWhen: { key: 'rent_partial_year', equals: true } },
        {
            key: 'rent_free_months_description',
            label: 'Hyresfria månader',
            type: 'textarea',
            help: 'Ange vilka månader som är hyresfria (t.ex. juni–augusti).',
            visibleWhen: { key: 'rent_partial_year', equals: true },
        },
        { key: 'rent_includes_member_fee', label: 'Avgift till hyresgästorganisation ingår', default: 'nej', type: 'select', required: true, options: toSlotOptions(MEMBER_FEE_OPTIONS) },
        {
            key: 'rent_includes_membership',
            default: 'nej',
            label: 'Medlemskap i förhandlande hyresgästorganisation ingår',
            type: 'select',
            required: true,
            options: toSlotOptions(MEMBERSHIP_OPTIONS),
        },
        { key: 'rent_type', label: 'Hyrestyp', default: 'total', type: 'select', required: true, options: toSlotOptions(RENT_TYPE_OPTIONS) },
    ],
    body: (input, ctx) => [
        ...rows([['Hyra (kr/mån)', [numVal(ctx, input, 'rent_amount')]]]),
        ...nodes(shown(ctx, input.rent_partial_year === true) &&
            p([
                t('Hyra utgår '),
                numVal(ctx, input, 'rent_months_per_year'),
                t(' månader per år. Hyresfria månader: '),
                textVal(input, 'rent_free_months_description', 'long'),
                t('.'),
            ])),
        ...rows([
            ['Hyrestyp', [optionVal(RENT_TYPE_OPTIONS, input, 'rent_type')]],
            ['Avgift till hyresgästorg. ingår', [optionVal(MEMBER_FEE_OPTIONS, input, 'rent_includes_member_fee')]],
            ['Medlemskap i hyresgästorg. ingår', [optionVal(MEMBERSHIP_OPTIONS, input, 'rent_includes_membership')]],
        ]),
    ],
});
export const tillagg = sectionBlock({
    blockKey: `${K}.tillagg`,
    ref: 'tillagg',
    title: 'Tillägg',
    summary: 'Heating, cold/hot water and household electricity (included or individually metered) and optional equipment.',
    legalReview: REVIEW,
    slots: [
        { key: 'heating_included', label: 'Uppvärmning', default: 'ingar', type: 'select', required: true, options: toSlotOptions(HEATING_OPTIONS) },
        { key: 'cold_water_included', label: 'Kallvatten', default: 'ingar', type: 'select', required: true, options: toSlotOptions(COLD_WATER_OPTIONS) },
        { key: 'hot_water_included', label: 'Varmvatten', default: 'ingar', type: 'select', required: true, options: toSlotOptions(HOT_WATER_OPTIONS) },
        { key: 'electricity_included', label: 'Hushållsel', default: 'eget_abonnemang', type: 'select', required: true, options: toSlotOptions(ELECTRICITY_OPTIONS) },
        { key: 'extras_included', label: 'Tillval av utrustning', default: false, type: 'boolean' },
        { key: 'extras_description', label: 'Beskrivning av tillval', type: 'textarea', visibleWhen: { key: 'extras_included', equals: true } },
    ],
    body: (input, ctx) => rows([
        ['Uppvärmning', [optionVal(HEATING_OPTIONS, input, 'heating_included')]],
        ['Kallvatten', [optionVal(COLD_WATER_OPTIONS, input, 'cold_water_included')]],
        ['Varmvatten', [optionVal(HOT_WATER_OPTIONS, input, 'hot_water_included')]],
        ['Hushållsel', [optionVal(ELECTRICITY_OPTIONS, input, 'electricity_included')]],
        ['Tillval av utrustning', [box(ctx, input.extras_included === true, 'extras_included')]],
        shown(ctx, input.extras_included === true) && ['Beskrivning', [textVal(input, 'extras_description', 'long')]],
    ]),
});
export const korridorstadning = sectionBlock({
    blockKey: `${K}.korridorstadning`,
    ref: 'korridorstadning',
    title: 'Korridor- och trappstädning',
    summary: 'Whether corridor and stair cleaning is included in the rent or arranged and paid for by the tenant.',
    legalReview: REVIEW,
    slots: [
        { key: 'corridor_cleaning', label: 'Korridor- och trappstädning', default: 'ingar', type: 'select', required: true, options: toSlotOptions(CORRIDOR_CLEANING_OPTIONS) },
    ],
    body: (input) => rows([['Städning', [optionVal(CORRIDOR_CLEANING_OPTIONS, input, 'corridor_cleaning')]]]),
});
const paysToAccount = (input) => optionChosen(PAYMENT_TYPE_OPTIONS, input.payment_type, 'plusgiro') || optionChosen(PAYMENT_TYPE_OPTIONS, input.payment_type, 'bankgiro');
export const betalning = sectionBlock({
    blockKey: `${K}.betalning`,
    ref: 'betalning',
    title: 'Hyrans betalning',
    summary: 'Rent is paid monthly in advance; payment method and account; reminder fees on late payment.',
    legalReview: REVIEW,
    slots: [
        { key: 'payment_type', label: 'Betalningssätt', default: 'avisering', type: 'select', required: true, options: toSlotOptions(PAYMENT_TYPE_OPTIONS) },
        { key: 'payment_account', label: 'Kontonummer', type: 'text', visibleWhen: whenOption('payment_type', optionOf(PAYMENT_TYPE_OPTIONS, 'plusgiro'), optionOf(PAYMENT_TYPE_OPTIONS, 'bankgiro')) },
    ],
    body: (input, ctx) => [
        p('Hyran ska betalas i förskott senast sista vardagen före varje kalendermånads början.'),
        ...rows([
            ['Betalningssätt', [optionVal(PAYMENT_TYPE_OPTIONS, input, 'payment_type')]],
            shown(ctx, paysToAccount(input)) && ['Kontonummer', [textVal(input, 'payment_account')]],
        ]),
        p('Vid försenad hyresbetalning ska hyresgästen betala ersättning för skriftlig betalningspåminnelse enligt lagstiftningen om ersättning för inkassokostnader.'),
    ],
});
export const deposition = sectionBlock({
    blockKey: `${K}.deposition`,
    ref: 'deposition',
    title: 'Deposition',
    summary: 'Whether a deposit is required, its size and repayment period, and what the deposit secures.',
    legalReview: REVIEW,
    slots: [
        { key: 'deposit_required', label: 'Deposition krävs', default: 'nej', type: 'select', required: true, options: toSlotOptions(DEPOSIT_OPTIONS) },
        { key: 'deposit_months', label: 'Depositionsbelopp (månadshyror)', type: 'number', visibleWhen: whenOption('deposit_required', optionOf(DEPOSIT_OPTIONS, 'ja')) },
        { key: 'deposit_return_days', label: 'Återbetalning inom (dagar)', default: 30, type: 'number', visibleWhen: whenOption('deposit_required', optionOf(DEPOSIT_OPTIONS, 'ja')) },
    ],
    body: (input, ctx) => {
        const required = shown(ctx, optionChosen(DEPOSIT_OPTIONS, input.deposit_required, 'ja'));
        return [
            ...rows([
                ['Deposition krävs', [optionVal(DEPOSIT_OPTIONS, input, 'deposit_required')]],
                required && ['Depositionsbelopp (månadshyror)', [numVal(ctx, input, 'deposit_months')]],
                required && ['Återbetalning inom (dagar)', [numVal(ctx, input, 'deposit_return_days')]],
            ]),
            ...nodes(required &&
                p('Hyresgästen ska till hyresvärden erlägga deposition senast i samband med tillträdet. Depositionen utgör säkerhet för hyresgästens samtliga förpliktelser enligt detta avtal, inklusive men inte begränsat till betalning av hyra, ersättning för skador på lägenheten samt andra kostnader hänförliga till hyresförhållandet.')),
        ];
    },
});
export const garage = sectionBlock({
    blockKey: `${K}.garage`,
    ref: 'garage',
    title: 'Garage / Bilplats',
    summary: 'Garage or parking arrangement and space number; the tenant may not assign the tenancy without written consent.',
    legalReview: REVIEW,
    slots: [
        { key: 'parking_arrangement', label: 'Garage / Bilplats', default: 'inget', type: 'select', required: true, options: toSlotOptions(PARKING_OPTIONS) },
        {
            key: 'parking_spot_number',
            label: 'Garage- / bilplatsnummer',
            type: 'text',
            visibleWhen: whenOption('parking_arrangement', optionOf(PARKING_OPTIONS, 'ingar_i_hyran')),
        },
    ],
    body: (input, ctx) => [
        ...rows([
            ['Upplåtelse', [optionVal(PARKING_OPTIONS, input, 'parking_arrangement')]],
            shown(ctx, optionChosen(PARKING_OPTIONS, input.parking_arrangement, 'ingar_i_hyran')) && [
                'Plats nr',
                [textVal(input, 'parking_spot_number')],
            ],
        ]),
        p('Hyresgästen får inte utan hyresvärdens skriftliga medgivande överlåta hyresrätten eller sätta annan i sitt ställe.'),
    ],
});
export const reservnycklar = sectionBlock({
    blockKey: `${K}.reservnycklar`,
    ref: 'reservnycklar',
    title: 'Reservnycklar',
    summary: 'Whether the tenant consents to the landlord holding a spare key.',
    legalReview: REVIEW,
    slots: [{ key: 'spare_key_consent', label: 'Reservnyckel — hyresgästens medgivande', default: 'medger', type: 'select', required: true, options: toSlotOptions(SPARE_KEY_OPTIONS) }],
    body: (input) => rows([['Hyresgästen medger hyresvärden rätt att inneha reservnyckel', [optionVal(SPARE_KEY_OPTIONS, input, 'spare_key_consent')]]]),
});
export const sarskildaBestammelser = sectionBlock({
    blockKey: `${K}.sarskilda-bestammelser`,
    ref: 'sarskilda-bestammelser',
    title: 'Särskilda bestämmelser',
    summary: 'The study-time rule (max ~5 years), furnished letting, and cleaning responsibility (corridor room vs apartment).',
    legalReview: REVIEW,
    slots: [
        { key: 'inventory_included', label: 'Lägenheten hyrs ut med inventarier och annan utrustning', default: false, type: 'boolean' },
        {
            key: 'inventory_description',
            label: 'Beskrivning av inventarier',
            type: 'textarea',
            help: 'Beskriv kortfattat de inventarier som ingår, eller hänvisa till bilaga.',
            visibleWhen: { key: 'inventory_included', equals: true },
        },
    ],
    body: (input, ctx) => {
        const items = [
            [
                'Lägenheten är avsedd för person som bedriver aktiva universitets-, högskole- eller därmed jämförliga studier. Prövning av hyresgästens behov av studentbostad kan därför ske under hyrestiden. Hyresförhållandet ska dock senast upphöra när hyresgästen är klar med sina studier. Med hänsyn till normalstudietid är avsikten att hyresförhållandet efter uppsägning ska upphöra efter maximalt 5 år. På hyresgästens skriftliga begäran kan hyresvärden bevilja förlängning under förutsättning att aktiva studier pågår eller att det finns andra synnerliga skäl till förlängning.',
            ],
        ];
        if (shown(ctx, input.inventory_included === true)) {
            items.push([
                t('Lägenheten hyrs ut med inventarier och annan utrustning enligt särskild förteckning: '),
                textVal(input, 'inventory_description', 'long'),
                t('.'),
            ]);
        }
        if (shown(ctx, isCorridor(input))) {
            items.push(['Hyresgästen ansvarar för städning av lägenheten och gemensamma utrymmen som ingår i upplåtelsen.']);
        }
        if (shown(ctx, input.is_corridor_room === false)) {
            items.push(['Hyresgästen ansvarar för städning av lägenheten.']);
        }
        return [list(items.map((item) => [p(item)]), { ordered: true })];
    },
});
export const rokforbud = unnumberedClause({
    blockKey: `${K}.rokforbud`,
    ref: 'rokforbud',
    title: 'Rökförbud',
    summary: 'Smoking is not allowed in the apartment or the building’s common areas.',
    legalReview: REVIEW,
    body: ['Rökning är inte tillåten i lägenheten eller husets allmänna och gemensamma utrymmen.'],
});
export const hyresgastensAtaganden = unnumberedClause({
    blockKey: `${K}.hyresgastens-ataganden`,
    ref: 'hyresgastens-ataganden',
    title: 'Hyresgästens åtaganden',
    summary: 'The tenant’s undertakings: duty of care, no subletting without consent, occupancy, waste, snow, access, viewings, move-out.',
    legalReview: REVIEW,
    body: [
        'Hyresgästen förbinder sig:',
        {
            bullets: [
                'att iaktta sin vårdplikt i sin användning av lägenheten och vad som hör till den samt vid användning av de gemensamma utrymmena.',
                'att inte utan hyresvärdens samtycke eller hyresnämndens tillstånd upplåta lägenheten i andra hand.',
                'att inte låta fler personer stadigvarande bo i lägenheten än vad den är avsedd för.',
                'att på egen bekostnad forsla bort avfall som är olämpligt för soptunna eller sopnedkast.',
                'att hålla till lägenheten hörande balkong eller uteplats fri från snö och is.',
                'att lämna hyresvärden tillträde till lägenheten för tillsyn eller förbättringsarbete.',
                'att när lägenheten sagts upp, låta den visas för ny hyresgäst på lämplig tid.',
                'att vid flytt överlämna samtliga port- och dörrnycklar samt städa lägenheten noggrant.',
            ],
        },
    ],
});
export const forceMajeure = unnumberedClause({
    blockKey: `${K}.force-majeure`,
    ref: 'force-majeure',
    title: 'Force majeure',
    summary: 'The landlord is excused for failures caused by war, riot, strikes, fire, explosion or public authority beyond its control.',
    legalReview: REVIEW,
    body: [
        'Hyresvärden fritar sig från skyldighet att fullgöra sin del av avtalet och från skyldighet att betala skadestånd om åtagandena inte alls eller endast till onormalt hög kostnad kan fullgöras på grund av krig, upplopp, arbetsinställelse, blockad, eldsvåda, explosion eller ingrepp av offentlig myndighet som hyresvärden inte råder över och inte heller kunnat förutse.',
    ],
});
export const tidigareHyresavtal = unnumberedClause({
    blockKey: `${K}.tidigare-hyresavtal`,
    ref: 'tidigare-hyresavtal',
    title: 'Tidigare hyresavtal',
    summary: 'Any earlier lease between the parties for this apartment ends when this one takes effect.',
    legalReview: REVIEW,
    body: [
        'Eventuellt tidigare hyresavtal mellan parterna avseende denna lägenhet upphör att gälla fr.o.m. dagen för detta avtals ikraftträdande.',
    ],
});
export const underskrifter = signingBlockDefinition({
    blockKey: `${K}.underskrifter`,
    summary: 'Signature panels for landlord and tenant, with the execution statement.',
    defaults: { ref: 'underskrift', title: 'Underskrifter' },
});
export const customSections = customSectionsBlockDefinition(`${K}.custom-sections`);
export const STUDENTBOSTAD_BLOCKS = [
    avtalsuppgifter,
    hyresvard,
    hyresgast,
    lagenhetensAdress,
    anvandning,
    storlek,
    tillbehor,
    hyrestid,
    uppsagning,
    hyressattningsmodell,
    forhandlingsklausul,
    hyra,
    tillagg,
    korridorstadning,
    betalning,
    deposition,
    garage,
    reservnycklar,
    sarskildaBestammelser,
    rokforbud,
    hyresgastensAtaganden,
    forceMajeure,
    tidigareHyresavtal,
    underskrifter,
    customSections,
];
//# sourceMappingURL=blocks.js.map